import { call, select, takeEvery } from 'redux-saga/effects';
import {
  DoRequestResponse,
  GlobalActions,
  ShopTarifierungRequestEvents,
  TarifiereAngebotForShopAction,
} from '@ps20-frontend/shop-integration-redux';
import {
  createHashedTaasData,
  ShopTarifierer,
  ShopTarifierungsErgebnisHandler,
} from '@ps20-frontend/preis-lib';
import { App } from '@ps20-frontend/frontend-models';
import { TaasRequestModel, TaasResponseModel, TaasVwgV1Data } from '@ps20/bff-models';
import { CallEffect, ForkEffect, SelectEffect } from '@redux-saga/core/effects';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { isGebaeudeYoungerThanFiveYears } from '../../app/shared/selectors/gebaeude.selector';
import { IAppState } from '../appState';
import { buildTaasVWGDataForShop } from './transformer/vwgData.transformer';

export const shopTarifierungsErgebnisHandler = new ShopTarifierungsErgebnisHandler<
  IAppState,
  TaasVwgV1Data
>(App.VWG, getVereinbarungInfo);
export const shopTarifierer = new ShopTarifierer({
  appId: App.VWG,
  taasDataBuilder: buildTaasVWGDataForShop,
  resultHandler: shopTarifierungsErgebnisHandler,
  hashFunc: createHashedTaasDataVWG,
});

export function* shopTarifierungVWG(): Generator<ForkEffect> {
  yield takeEvery(
    GlobalActions.TARIFIERE_ANGEBOT_FOR_SHOP,
    safeSaga(callTarifierungOnMatchingAngebotsId, App.VWG),
  );
  yield takeEvery(
    ShopTarifierungRequestEvents.PREISE_SHOP_REQUESTED_SUCCESSFUL_VWG,
    safeSaga(callSuccessOnMatchingAngebotsId, App.VWG),
  );
}

export function* callTarifierungOnMatchingAngebotsId(
  tarifiereAngebotAction: TarifiereAngebotForShopAction,
): Generator<SelectEffect | CallEffect> {
  const appState: IAppState = yield select();
  const angebotsIdIsMatching = appState.angebotsId === tarifiereAngebotAction.payload.angebotsId;

  if (angebotsIdIsMatching) {
    yield call(shopTarifierer.callTarifierung, tarifiereAngebotAction);
  }
}

export function* callSuccessOnMatchingAngebotsId(
  successAction: DoRequestResponse<TaasResponseModel, TaasRequestModel<TaasVwgV1Data>>,
): Generator<SelectEffect | CallEffect> {
  const appState = (yield select()) as IAppState;
  const originEvent: TarifiereAngebotForShopAction =
    successAction.doRequestAction.payload.originEvent;
  const angebotsIdIsMatching = appState.angebotsId === originEvent.payload.angebotsId;

  if (angebotsIdIsMatching) {
    yield call(shopTarifierer.handleSuccess, successAction);
  }
}

export function getVereinbarungInfo(state: IAppState): string {
  const risikoort = state.risikoort;
  const wohnflaeche = state.gebaeude.wohnflaeche;
  const risikoortString = `${risikoort.strasse} ${risikoort.hausnummer}, ${risikoort.plz} ${risikoort.ort}, `;
  const wohnflaecheString = `${wohnflaeche} m²`;
  return `${risikoortString}${wohnflaecheString}`;
}

export function createHashedTaasDataVWG(taasData: TaasVwgV1Data): string {
  const relevantData = { ...taasData };
  relevantData.gisDaten = undefined;
  if (isGebaeudeYoungerThanFiveYears(relevantData.gebaeude.baujahr)) {
    relevantData.vorversicherung = undefined;
  }
  if (taasData.gebaeude?.adresse) {
    relevantData.gebaeude.adresse = {
      plz: taasData.gebaeude.adresse.plz,
      ort: taasData.gebaeude.adresse.ort,
      strasse: taasData.gebaeude.adresse.strasse,
      hausnummer: taasData.gebaeude.adresse.hausnummer,
      latitude: taasData.gebaeude.adresse.latitude,
      longtitude: taasData.gebaeude.adresse.longtitude,
      auslaendischeAdresse: taasData.gebaeude.adresse.auslaendischeAdresse,
    };
  }
  return createHashedTaasData(relevantData);
}
